import { useGetAppointmentFilterOptions, useListAppointments } from '@pa/api/clinic'
import {
  Avatar,
  DateRangeFilter,
  FilterMenu,
  Pager,
  PAGE_SIZE,
  Segmented,
  todayIn,
  usePersistentFlag,
  useScrollReset,
  useToast,
  type DateRangeValue,
} from '@pa/ui'
import { keepPreviousData } from '@tanstack/react-query'
import { Building2, CalendarCheck2, ChartLine, EyeOff, Search, Stethoscope, Tag } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { ONLY_CONFLICTS } from '../model/conflicts'
import { useOpenInPf } from '../model/openInPf'
import {
  hasFilters,
  pfQuery,
  presetsFor,
  Q_MAX,
  rangeValueOf,
  statsOf,
  toListParams,
  UNKNOWN_SOURCE,
  viewOf,
  whenOf,
  type AppointmentsSearch,
  type When,
} from '../model/search'
import { AppointmentDrawer } from './AppointmentDrawer'
import { BookingStatsCard, STATS_HIDDEN_KEY } from './BookingStatsCard'
import { BookedTable, DayTable, shortLocation, TypeIcon } from './tables'

/** Typing in the search box updates the URL after 300 ms, with `replace` (Frontend §6.1). */
export const SEARCH_DEBOUNCE_MS = 300

export interface AppointmentsPageProps {
  clinicId: string
  /** The clinic's time zone: dates, presets and day groups are in clinic time (D-21). */
  timeZone: string
  search: AppointmentsSearch
  onSearch: (next: AppointmentsSearch, opts?: { replace?: boolean }) => void
}

/**
 * C-01 Appointments (UserFlows C-01) with the booking stats card (task 09, #16), ported from the mockup's
 * `admin/Appointments.tsx`: purpose copy, the view switch, the filters (all in the URL), Clear filters, the booked
 * table or the day-grouped table, the numbered pager, and loading / empty / error-with-Retry states. A row opens
 * C-01a (`?appt=`). Data: #15 list (server-side filters, sort and paging), #17 filter options.
 */
export function AppointmentsPage({ clinicId, timeZone, search, onSearch }: AppointmentsPageProps) {
  const view = viewOf(search)
  const today = todayIn(timeZone)
  const q = pfQuery(search.q)
  const params = toListParams(search, timeZone, today)
  const list = useListAppointments(clinicId, params, {
    query: { enabled: q !== null, placeholderData: keepPreviousData },
  })
  const options = useGetAppointmentFilterOptions(clinicId)
  // "Open in Practice Fusion" on a row (task 23): a failure shows as a toast, and the blank tab is closed.
  const toast = useToast()
  const pf = useOpenInPf(
    clinicId,
    useCallback(
      (message: string) => {
        toast(message, 'error')
      },
      [toast],
    ),
  )
  const [draft, setDraft] = useState(search.q ?? '')
  // The stats card can be hidden to give the table more room; remembered on this device (UserFlows C-01).
  const [statsHidden, setStatsHidden] = usePersistentFlag(STATS_HIDDEN_KEY, false)

  /** A filter change: page back to 1, the drawer closed. */
  const set = (patch: Partial<AppointmentsSearch>, opts?: { replace?: boolean }) => {
    const merged: Record<string, unknown> = { ...search, ...patch, page: undefined, appt: undefined }
    const next = Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined)) as AppointmentsSearch
    onSearch(next, opts)
  }

  // The search box: the URL follows after 300 ms (replace).
  useEffect(() => {
    if (draft === (search.q ?? '')) return
    const t = setTimeout(() => {
      set({ q: draft.trim() === '' ? undefined : draft.slice(0, Q_MAX) }, { replace: true })
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on typing only
  }, [draft])

  const rowsRef = useScrollReset<HTMLDivElement>([search.page, view, params.from, params.to, params.q])
  // Day headings stick just under the column header, so they need its height (mockup).
  useLayoutEffect(() => {
    const wrap = rowsRef.current
    const head = wrap?.querySelector('thead')
    if (wrap && head) wrap.style.setProperty('--thead-h', `${String(head.offsetHeight)}px`)
  })

  const opts = options.data
  const sourceOptions = opts
    ? [
        ...opts.sources.saved.map((s) => ({ value: s, label: s })),
        ...opts.sources.unlisted.map((s) => ({ value: s, label: `${s} (not in list)` })),
        ...(opts.sources.hasUnknown ? [{ value: UNKNOWN_SOURCE, label: 'Unknown' }] : []),
      ]
    : []

  const page = list.data
  const items = q === null ? [] : (page?.items ?? [])
  const total = q === null ? 0 : (page?.total ?? 0)
  const open = (id: string) => {
    onSearch({ ...search, appt: id })
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="title-row">
            <h1>Appointments</h1>
            <button
              type="button"
              className="btn btn-secondary btn-sm stats-toggle"
              aria-pressed={!statsHidden}
              onClick={() => {
                setStatsHidden(!statsHidden)
              }}
            >
              {statsHidden ? (
                <>
                  <ChartLine aria-hidden /> View stats
                </>
              ) : (
                <>
                  <EyeOff aria-hidden /> Hide stats
                </>
              )}
            </button>
          </div>
          <p>
            Every appointment booked online, as written to Practice Fusion. No patient details are kept here: use the PF
            appointment ID to find the patient in PF.
          </p>
        </div>
      </div>

      <div className="appt-layout">
        {!statsHidden && (
          <BookingStatsCard
            clinicId={clinicId}
            period={statsOf(search)}
            onPeriod={(p) => {
              const next: AppointmentsSearch = { ...search }
              if (p === 'month') delete next.stats
              else next.stats = p
              onSearch(next, { replace: true })
            }}
          />
        )}
        <div className="card card-fill">
          <div className="toolbar view-bar">
            <Segmented
              label="View"
              value={view}
              onChange={(v) => {
                // Switching views resets the date filter (and `when`, which belongs to the appointment view).
                set({
                  view: v === 'appointment' ? 'appointment' : undefined,
                  range: undefined,
                  from: undefined,
                  to: undefined,
                  when: undefined,
                })
              }}
              options={[
                { value: 'booked', label: 'By booked date' },
                { value: 'appointment', label: 'By appointment date' },
              ]}
            />
            <span className="hint">
              {view === 'booked' ? 'Newest bookings first' : 'Grouped by the day of the visit'}
            </span>
          </div>
          <div className="toolbar filters">
            <div className="search input-wrap">
              <Search aria-hidden />
              <input
                className="input"
                aria-label="Search PF appointment ID"
                placeholder="Search PF appointment ID"
                maxLength={Q_MAX}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value)
                }}
              />
            </div>
            {view === 'appointment' && (
              <Segmented<When>
                label="When"
                value={whenOf(search)}
                onChange={(w) => {
                  set({ when: w === 'upcoming' ? undefined : w })
                }}
                options={[
                  { value: 'upcoming', label: 'Upcoming' },
                  { value: 'past', label: 'Past' },
                  { value: 'all', label: 'All' },
                ]}
              />
            )}
            <DateRangeFilter
              key={view}
              label={view === 'booked' ? 'Booked' : 'Date'}
              value={rangeValueOf(search)}
              presets={presetsFor(view)}
              today={today}
              allowFuture={view === 'appointment'}
              onChange={(r: DateRangeValue) => {
                set(
                  r.id === 'any'
                    ? { range: undefined, from: undefined, to: undefined }
                    : { range: r.id, from: r.from, to: r.to },
                )
              }}
            />
            <FilterMenu
              label="Source"
              icon={<Tag aria-hidden />}
              value={search.source ?? null}
              onChange={(v) => {
                set({ source: v ?? undefined })
              }}
              allLabel="All sources"
              options={sourceOptions}
            />
            <FilterMenu
              label="Visit type"
              icon={<Stethoscope aria-hidden />}
              value={search.type ?? null}
              onChange={(v) => {
                set({ type: v ?? undefined })
              }}
              allLabel="All visit types"
              options={(opts?.visitTypes ?? []).map((t) => ({
                value: t.id,
                label: t.name,
                icon: <TypeIcon telehealth={t.telehealth} size={15} />,
              }))}
            />
            <FilterMenu
              label="Location"
              icon={<Building2 aria-hidden />}
              value={search.location ?? null}
              onChange={(v) => {
                set({ location: v ?? undefined })
              }}
              allLabel="All locations"
              options={(opts?.locations ?? []).map((l) => ({ value: l.id, label: shortLocation(l.name) }))}
            />
            <FilterMenu
              label="Provider"
              icon={<Stethoscope aria-hidden />}
              value={search.provider ?? null}
              onChange={(v) => {
                set({ provider: v ?? undefined })
              }}
              allLabel="All providers"
              options={(opts?.providers ?? []).map((p) => ({
                value: p.id,
                label: p.name,
                icon: <Avatar name={p.name} color={p.color} size="sm" />,
              }))}
            />
            <label className="check-row conflict-filter">
              <input
                type="checkbox"
                checked={search.conflict === true}
                onChange={(e) => {
                  set({ conflict: e.target.checked ? true : undefined })
                }}
              />
              {ONLY_CONFLICTS}
            </label>
            {hasFilters(search) && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setDraft('')
                  // Clear filters keeps the view and the stats period (Frontend §6.1).
                  onSearch({
                    ...(view === 'appointment' && { view }),
                    ...(statsOf(search) !== 'month' && { stats: statsOf(search) as 'week' | '3m' }),
                  })
                }}
              >
                Clear filters
              </button>
            )}
          </div>
          <div ref={rowsRef} className="table-wrap" aria-busy={list.isFetching}>
            {list.isError ? (
              <div className="empty" role="alert">
                <CalendarCheck2 aria-hidden />
                <div>Couldn&apos;t load the appointments.</div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    void list.refetch()
                  }}
                >
                  Retry
                </button>
              </div>
            ) : q !== null && list.isPending ? (
              <div className="empty" role="status">
                <span className="spinner" aria-hidden />
                <div>Loading appointments…</div>
              </div>
            ) : (
              <>
                {items.length > 0 &&
                  (view === 'booked' ? (
                    <BookedTable rows={items} timeZone={timeZone} onOpen={open} pf={pf} />
                  ) : (
                    <DayTable
                      rows={items}
                      timeZone={timeZone}
                      onOpen={open}
                      pf={pf}
                      dayTotals={page?.dayTotals}
                      today={today}
                    />
                  ))}
                {items.length === 0 && (
                  <div className="empty">
                    <CalendarCheck2 aria-hidden />
                    <div>No appointments match these filters</div>
                  </div>
                )}
              </>
            )}
          </div>
          {!list.isError && (
            <Pager
              page={page?.page ?? search.page ?? 1}
              pageSize={page?.pageSize ?? PAGE_SIZE}
              total={total}
              noun="appointments"
              onPage={(n) => {
                const next: AppointmentsSearch = { ...search }
                delete next.appt
                if (n > 1) next.page = n
                else delete next.page
                onSearch(next)
              }}
            />
          )}
        </div>
      </div>

      {search.appt && (
        <AppointmentDrawer
          clinicId={clinicId}
          appointmentId={search.appt}
          timeZone={timeZone}
          onClose={() => {
            const next = { ...search }
            delete next.appt
            onSearch(next, { replace: true })
          }}
        />
      )}
    </>
  )
}
